import { Box, Grid } from '@mui/material';
import { getNomineesByPeriodAnalyticsQuery } from 'lib/api/fundsDashboard/fundsDashboard';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import TableCard from 'lib/designSystem/Card/TableCard';
import LineChart from 'lib/designSystem/Charts/LineChart';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { CHART_COLORS } from 'lib/functions/const/colors';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

type SeriesType = 'amount' | 'nominees' | 'resells';

const TOGGLE_SERIES_OPTIONS = [
  { value: 'amount', label: 'Monto', ariaLabel: 'amount' },
  { value: 'nominees', label: 'Nóminas', ariaLabel: 'nominees' },
  { value: 'resells', label: 'Reventas', ariaLabel: 'resells' },
];

const SERIES_LABELS: Record<SeriesType, string> = {
  amount: 'Monto',
  nominees: 'Nóminas',
  resells: 'Reventas',
};

type PeriodType = 'month' | 'week';

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Mensual' },
  { value: 'week', label: 'Semanal' },
];

const NomineesByPeriod: React.FC = () => {
  const dispatch = useDispatch();
  const [startOperationDate, setStartOperationDate] = useState<string | null>(
    null,
  );
  const [endOperationDate, setEndOperationDate] = useState<string | null>(null);
  const [seriesType, setSeriesType] = useState<SeriesType>('amount');
  const [period, setPeriod] = useState<PeriodType>('month');

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (startOperationDate)
      params.set('startOperationDate', startOperationDate);
    if (endOperationDate) params.set('endOperationDate', endOperationDate);
    return params.toString();
  }, [period, startOperationDate, endOperationDate]);

  const { data, isPending, isError, refetch } =
    getNomineesByPeriodAnalyticsQuery(dispatch, query);

  const searchFilters: Filter[] = [
    {
      type: 'select',
      label: 'Período',
      value: period,
      setter: (value: PeriodType) => setPeriod(value),
      options: PERIOD_OPTIONS,
    },
    {
      type: 'date',
      label: 'Fecha operación inicio',
      value: startOperationDate,
      setter: setStartOperationDate,
      dateKey: 'startOperationDate',
      pairKey: 'endOperationDate',
    },
    {
      type: 'date',
      label: 'Fecha operación término',
      value: endOperationDate,
      setter: setEndOperationDate,
      dateKey: 'endOperationDate',
      pairKey: 'startOperationDate',
    },
  ];

  const lineChartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((item) => ({
      name: item.period,
      [seriesType]: item[seriesType],
    }));
  }, [data, seriesType]);

  const lineChartColors = useMemo(() => [CHART_COLORS.blue], []);

  const handleTriggerSearch = async () => {
    await refetch();
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={searchFilters}
          triggerSearch={handleTriggerSearch}
          dispatch={dispatch}
          showActionButtons={false}
        />
      ),
    },
  ];

  return (
    <Box>
      <HeaderWithActions
        title="Nóminas por período"
        variant="body1"
        buttons={buttons}
      />
      <Grid container>
        <Grid item xs={12}>
          <TableCard>
            <SubtitleWithTooltip
              title="Evolución"
              tooltipContent={`Serie mostrada: ${SERIES_LABELS[seriesType]}. Monto en millones, nóminas y reventas en cantidades.`}
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={seriesType}
                onChange={(value) => setSeriesType(value as SeriesType)}
                options={TOGGLE_SERIES_OPTIONS}
              />
            </SubtitleWithTooltip>
            {isPending && (
              <Box
                sx={{
                  minHeight: 320,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CenteredCircularProgress />
              </Box>
            )}
            {!isPending && isError && (
              <Box sx={{ py: 2, color: 'error.main' }}>
                Error al cargar nóminas por período.
              </Box>
            )}
            {!isPending && !isError && data !== undefined && (
              <>
                {data.length === 0 ? (
                  <Box
                    sx={{
                      py: 2,
                      color: 'text.secondary',
                      textAlign: 'center',
                    }}
                  >
                    No hay datos de nóminas por período.
                  </Box>
                ) : (
                  <LineChart
                    key={`nomineesByPeriod-${seriesType}`}
                    data={lineChartData}
                    colors={lineChartColors}
                    showLegend={false}
                    height={290}
                    showArea={false}
                    yAxisFormat={seriesType === 'amount' ? 'M' : 'auto'}
                    dashLastSeries={true}
                  />
                )}
              </>
            )}
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NomineesByPeriod;
